import {
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

import { combineLatest, map, Observable, of, Subscription, take } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LanguageService } from '@fcom/ui-translate';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import {
  ButtonTheme,
  IconButtonSize,
  IconButtonTheme,
  IconPosition,
  PopoverOptions,
  PopoverPosition,
} from '@fcom/ui-components';
import { CountryState } from '@fcom/core/interfaces';
import { allowedConfs } from '@fcom/core/reducers/langs';
import { unsubscribe } from '@fcom/core/utils';
import { NativeBridgeService } from '@fcom/core';
import { finShare } from '@fcom/rx';
import { MediaQueryService } from '@fcom/common/services';

import { PopoverService } from '../../../ui-components/components/popover';
import { ElementActions, ElementTypes, GaContext } from '../../interfaces';
import { CountryAndLanguageSelection } from '../country-and-language-selector/country-and-language-selector.component';
import { PageMetaService } from '../../services/page-meta/page-meta.service';
import { getLocalizedLinkForLangCode } from '../../utils/language.utils';

export enum LanguageSelectorContext {
  BOOKING,
  TOP_NAVI,
  APP,
}

@Component({
  selector: 'fin-language-selector',
  templateUrl: './language-selector.component.html',
  styleUrls: [],
  standalone: false,
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
  private platform = inject(PLATFORM_ID);
  private languageService = inject(LanguageService);
  private router = inject(Router);
  private pageMetaService = inject(PageMetaService);
  private gtmService = inject(GtmService);
  private popoverService = inject(PopoverService);
  private nativeBridgeService = inject(NativeBridgeService);
  private mediaQueryService = inject(MediaQueryService);

  styles: CSSStyleDeclaration;
  popoverOptions: PopoverOptions;

  readonly ButtonTheme = ButtonTheme;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  readonly currentLanguage$: Observable<string>;

  hideLanguageTitle = input<boolean>(false);

  @Input()
  hideLanguageOptions = false;

  @Input()
  lockLanguageSelection = false;

  @Input()
  hideTitle = false;

  @Input()
  showCountryNameInMobile = false;

  @Input()
  skipRedirect = false;

  @Input()
  currency$: Observable<string | undefined> = of(undefined);

  @Input()
  context: LanguageSelectorContext;

  @Output()
  applySelection = new EventEmitter<CountryAndLanguageSelection>();

  country$: Observable<CountryState>;
  currencySymbol$: Observable<string>;
  subscriptions: Subscription = new Subscription();

  private isTabletDown: Signal<boolean>;

  constructor() {
    this.currentLanguage$ = this.languageService.langKey;
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platform)) {
      this.styles = getComputedStyle(document.documentElement);
      this.popoverOptions = {
        popoverWidth: 340,
        showArrowCaret: false,
        showLeftBorder: false,
        roundedCorners: true,
        hasPadding: true,
        fullWidthContent: true,
        showFullscreenOnBreakpoint: 'tablet_down',
        popoverPosition: PopoverPosition.BOTTOM,
        useFixedPosition: true,
        zIndex: parseInt(this.styles.getPropertyValue('--z-index-nav')),
      };
    }
    this.country$ = this.initCountry();
    this.currencySymbol$ = combineLatest([this.country$, this.currency$]).pipe(
      map(([country, currency]) => country?.currencySymbol ?? currency),
      map((currencySymbol) => (currencySymbol ? `(${currencySymbol})` : '')),
      finShare()
    );
    this.isTabletDown = this.mediaQueryService.isBreakPoint('tablet_down');
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  openLanguageSelector(): void {
    this.gtmService.trackElement(
      'language-selector-open',
      this.contextToGaContext[this.context],
      ElementTypes.BUTTON,
      undefined,
      ElementActions.OPEN
    );
  }

  closeLanguageSelector(): void {
    this.popoverService.close();
  }

  selectLanguageAndRedirect(selection: CountryAndLanguageSelection): void {
    this.gtmService.trackElement(
      'language-selector-select',
      this.contextToGaContext[this.context],
      ElementTypes.BUTTON,
      selection.languageCode,
      ElementActions.SUBMIT
    );

    if (!this.skipRedirect && this.languageService.langValue !== selection.languageCode) {
      switch (this.context) {
        case LanguageSelectorContext.BOOKING:
          this.subscriptions.add(
            this.pageMetaService.prevPageUrl$.pipe(take(1)).subscribe((prevPageUrl) => {
              this.router.navigateByUrl(`/${selection.languageCode}${prevPageUrl}`);
            })
          );
          break;
        case LanguageSelectorContext.TOP_NAVI:
          this.subscriptions.add(
            this.pageMetaService.links.pipe(take(1)).subscribe((links) => {
              const url = getLocalizedLinkForLangCode(selection.languageCode, links, this.getPreviousUrl());
              this.router.navigateByUrl(url, { onSameUrlNavigation: 'reload' });
            })
          );
          break;
      }
    }

    this.applySelection.emit(selection);

    this.closeLanguageSelector();
  }

  private initCountry(): Observable<CountryState> {
    return this.languageService.countryCode.pipe(
      map(
        (countryCode) =>
          allowedConfs.find((country) => country.code === countryCode) || {
            languages: [],
            name: '',
            code: '',
          }
      ),
      finShare()
    );
  }

  private getPreviousUrl(): string | undefined {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/corporate/')) {
      return `/${currentUrl.split('/').slice(2).join('/')}`;
    }
  }

  private get contextToGaContext(): Record<LanguageSelectorContext, GaContext> {
    const isInsideNativeWebView = this.nativeBridgeService.isInsideNativeWebView;

    return {
      [LanguageSelectorContext.BOOKING]: GaContext.BOOKING_FLOW,
      [LanguageSelectorContext.TOP_NAVI]:
        isInsideNativeWebView || this.isTabletDown()
          ? GaContext.MOBILE_MAIN_NAVIGATION
          : GaContext.DESKTOP_MAIN_NAVIGATION,
      [LanguageSelectorContext.APP]: GaContext.MOBILE_APP,
    };
  }
}
